import { useState, useEffect } from 'react';

const BIOMETRIC_KEY = 'bi_biometric_enrolled';
const BIOMETRIC_CRED = 'bi_biometric_cred_id';

// Check if a real platform authenticator (Face ID, fingerprint) is available
// This is async and returns true only on mobile/devices with biometric hardware
async function checkPlatformAuthenticator() {
  try {
    if (!window.PublicKeyCredential) return false;
    if (!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    window.__biometricAvailable = available;
    return available;
  } catch(e) {
    window.__biometricAvailable = false;
    return false;
  }
}

// Run check on load
checkPlatformAuthenticator();

function isBiometricSupported() {
  return !!window.__biometricAvailable;
}

// Convert base64url string to ArrayBuffer
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  return buffer;
}

// Convert ArrayBuffer to base64url string
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function enrollBiometric() {
  // Generate a random user ID for this device
  const userId = crypto.getRandomValues(new Uint8Array(16));
  
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: { name: 'Bearing Intelligence', id: window.location.hostname },
      user: {
        id: userId,
        name: 'bearing-user',
        displayName: 'Bearing Intelligence User',
      },
      pubKeyCredParams: [
        { alg: -7,  type: 'public-key' }, // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // device biometric only
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60000,
      attestation: 'none',
    }
  });

  // Store credential ID for future verification
  const credId = bufferToBase64url(credential.rawId);
  localStorage.setItem(BIOMETRIC_KEY, 'true');
  localStorage.setItem(BIOMETRIC_CRED, credId);
  return true;
}

async function verifyBiometric() {
  const credIdStr = localStorage.getItem(BIOMETRIC_CRED);
  if (!credIdStr) return false;

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: window.location.hostname,
      allowCredentials: [{
        id: base64urlToBuffer(credIdStr),
        type: 'public-key',
        transports: ['internal'],
      }],
      userVerification: 'required',
      timeout: 60000,
    }
  });

  return !!assertion;
}

export function useBiometricGate() {
  const [supported, setSupported] = useState(!!window.__biometricAvailable);
  const enrolled = localStorage.getItem(BIOMETRIC_KEY) === 'true';

  useEffect(() => {
    checkPlatformAuthenticator().then(setSupported);
  }, []);

  return { enrolled, supported };
}

export function BiometricGate({ onVerified, onSkip }) {
  const [state,   setState]   = useState('idle'); // idle|verifying|enrolling|error|unsupported
  const [error,   setError]   = useState('');
  const supported = isBiometricSupported();

  useEffect(() => {
    if (!supported) { setState('unsupported'); return; }
    const enrolled = localStorage.getItem(BIOMETRIC_KEY) === 'true';
    // Auto-trigger verification if already enrolled
    if (enrolled) handleVerify();
  }, []); // eslint-disable-line

  const handleVerify = async () => {
    setState('verifying');
    setError('');
    try {
      const ok = await verifyBiometric();
      if (ok) onVerified();
      else { setState('error'); setError('Verification failed. Please try again.'); }
    } catch(e) {
      if (e.name === 'NotAllowedError') {
        setState('error');
        setError('Biometric verification was cancelled or timed out.');
      } else {
        setState('error');
        setError('Biometric verification failed: ' + e.message);
      }
    }
  };

  const handleEnroll = async () => {
    setState('enrolling');
    setError('');
    try {
      await enrollBiometric();
      onVerified();
    } catch(e) {
      if (e.name === 'NotAllowedError') {
        setState('error');
        setError('Enrollment was cancelled. You can set up biometrics later in settings.');
      } else {
        setState('error');
        setError('Enrollment failed: ' + e.message);
      }
    }
  };

  const enrolled = localStorage.getItem(BIOMETRIC_KEY) === 'true';

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0f1419',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        backgroundColor: '#1a1f2e',
        border: '1px solid rgba(196,165,99,0.25)',
        borderRadius: 12,
        padding: '32px 28px',
        maxWidth: 400, width: '100%',
        textAlign: 'center',
      }}>
        {/* Logo */}
        <img src="/BI_logo.png" alt="Bearing Intelligence"
          style={{ height: 48, marginBottom: 16 }} />

        <div style={{ color: '#c4a563', fontFamily: 'Georgia,serif',
          fontSize: 20, fontWeight: 'bold', marginBottom: 6 }}>
          Bearing Intelligence
        </div>

        {state === 'unsupported' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔓</div>
            <p style={{ color: '#8a8b8f', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
              Biometric authentication is not supported on this device or browser.
              Your session will continue without biometric verification.
            </p>
            <button onClick={onSkip} style={{
              width: '100%', padding: '11px 0', borderRadius: 6,
              backgroundColor: 'rgba(196,165,99,0.15)',
              border: '1px solid #c4a563', color: '#c4a563',
              cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
            }}>Continue without biometrics</button>
          </>
        )}

        {(state === 'idle' || state === 'error') && !enrolled && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
            <p style={{ color: '#8a8b8f', fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>
              Set up biometric authentication to secure your access to Bearing Intelligence.
              Your device handles all biometric data — nothing is sent to our servers.
            </p>
            <p style={{ color: '#555', fontSize: 11, lineHeight: 1.6, marginBottom: 20 }}>
              Uses Face ID, Touch ID, or your device's built-in biometric system.
            </p>
            {error && (
              <div style={{ padding: '8px 10px', backgroundColor: 'rgba(255,85,85,0.1)',
                border: '1px solid rgba(255,85,85,0.3)', borderRadius: 5,
                color: '#ff8888', fontSize: 12, marginBottom: 14 }}>{error}</div>
            )}
            <button onClick={handleEnroll} style={{
              width: '100%', padding: '11px 0', borderRadius: 6, marginBottom: 8,
              backgroundColor: 'rgba(196,165,99,0.15)',
              border: '1px solid #c4a563', color: '#c4a563',
              cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
            }}>Set up biometric access</button>
            <button onClick={onSkip} style={{
              width: '100%', padding: '9px 0', borderRadius: 6,
              backgroundColor: 'transparent',
              border: '1px solid rgba(255,255,255,0.08)', color: '#555',
              cursor: 'pointer', fontSize: 12,
            }}>Skip for now</button>
          </>
        )}

        {(state === 'idle' || state === 'verifying' || state === 'error') && enrolled && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>
              {state === 'verifying' ? '⏳' : '🔐'}
            </div>
            <p style={{ color: '#8a8b8f', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
              {state === 'verifying'
                ? 'Waiting for biometric verification…'
                : 'Verify your identity to access Bearing Intelligence.'}
            </p>
            {error && (
              <div style={{ padding: '8px 10px', backgroundColor: 'rgba(255,85,85,0.1)',
                border: '1px solid rgba(255,85,85,0.3)', borderRadius: 5,
                color: '#ff8888', fontSize: 12, marginBottom: 14 }}>{error}</div>
            )}
            {state !== 'verifying' && (
              <>
                <button onClick={handleVerify} style={{
                  width: '100%', padding: '11px 0', borderRadius: 6, marginBottom: 8,
                  backgroundColor: 'rgba(196,165,99,0.15)',
                  border: '1px solid #c4a563', color: '#c4a563',
                  cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
                }}>Verify with biometrics</button>
                <button onClick={() => {
                  localStorage.removeItem(BIOMETRIC_KEY);
                  localStorage.removeItem(BIOMETRIC_CRED);
                  onSkip();
                }} style={{
                  width: '100%', padding: '9px 0', borderRadius: 6,
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.08)', color: '#444',
                  cursor: 'pointer', fontSize: 11,
                }}>Remove biometrics and continue</button>
              </>
            )}
          </>
        )}

        {state === 'enrolling' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <p style={{ color: '#8a8b8f', fontSize: 13, lineHeight: 1.7 }}>
              Follow your device's prompt to set up biometric access…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
